"use client"
import { useState, useCallback, useMemo, useEffect, useRef } from "react"
import { usePersistedState } from "@/hooks/use-persisted-state"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { WorkflowGrid } from "@/components/workflow-grid"
import { MaintenanceBanner } from "@/components/maintenance-banner"
import { ClientRequestSubTabContent } from "@/components/client-request/client-request-sub-tab-content"
import { RequestsGroupStateProvider, useRequestsGroupState } from "@/contexts/requests-group-state-context"
import { RequestsTabHeaderSlotProvider } from "@/contexts/requests-tab-header-slot-context"
import { AtsSubTabContent } from "@/components/ats/ats-sub-tab-content"
import { VmsPlacementsTab } from "@/components/vms/tab"
import { FilterDrawer } from "@/components/filter-drawer"
import { ViewSelector } from "@/components/view-selector"
import { ViewManager } from "@/components/view-manager/view-manager"
import { WorkerSearchModal } from "@/components/worker-search-modal"
import { Button } from "@/components/ui/button"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Search, ChevronDown, Mail } from "lucide-react"
import { MessagesInboxModal } from "@/components/messages-inbox-modal"
import { MessageNotificationsModal } from "@/components/message-notifications-modal"
import { ClutterlessTheme } from "@/lib/message-themes"
import { Z_INDEX } from "@/lib/z-index"
import type { InboxConversation } from "@/hooks/use-inbox-conversations"
import { WorkflowProvider, useWorkflow } from "@/context/workflow-context-simple"
import { EntityTypeProvider } from "@/contexts/entity-type-context"
import type { WorkflowStage } from "@/types/workflow"
import { useLocalizedField } from '@/utils/language-utils'
import { useWorkflowTracking } from "@/hooks/use-workflow-tracking"
import { useTranslation } from "@/hooks/use-translation"
import { useStageCountsInit } from "@/hooks/use-stage-facets"
import { useStageMessageCounts, type StageMessageCount } from "@/hooks/use-stage-message-counts"
import { useRequestMessageCounts } from "@/hooks/use-request-message-counts"
import { useMessageNotifications } from "@/hooks/use-message-notifications"
import { useAuth } from "@/contexts/auth-context"
import { useCodeValues } from "@/hooks/use-code-values"
import { useFeatureFlags } from "@/contexts/feature-flag-context"
import { env } from "@/lib/env"
import { readDeepLinkParams, peekDeepLinkParams, isDeepLinkApplied, markDeepLinkApplied } from "@/lib/deep-link-params"
import { MessageComponentsProvider, EntityChatPanel } from "@gorilla-works/messages"

// V2: Feature flag to show/hide worker search (set to false to disable)
const ENABLE_WORKER_SEARCH = true;

const CLIENT_REQUESTS_TAB = "client-requests" as const
const ATS_PLACEMENTS_TAB = "ats-placements" as const
const VMS_PLACEMENTS_TAB = "vms-placements" as const

/**
 * Entity type behind each Requests-group tab — the key a scope must be stashed
 * under for that tab's grid to adopt it. WorkOrder stage tabs aren't listed:
 * they all share the root provider's 'WorkOrder' context.
 */
const REQUESTS_TAB_ENTITY_TYPES: Record<string, string> = {
  [CLIENT_REQUESTS_TAB]: 'ClientRequest',
  [ATS_PLACEMENTS_TAB]: 'AtsPlacementRequest',
  [VMS_PLACEMENTS_TAB]: 'VmsRequest',
}
const ROOT_ENTITY_TYPE = 'WorkOrder'

// The request-message entity types that roll into each Requests-group tab's unread badge. ATS/VMS are one
// type each; Client Requests also carries the onboarding families (`Request`/`OnboardingRequest`).
const TAB_MESSAGE_ENTITY_TYPES: Record<string, string[]> = {
  [ATS_PLACEMENTS_TAB]: ['AtsPlacementRequest'],
  [VMS_PLACEMENTS_TAB]: ['VmsRequest'],
  [CLIENT_REQUESTS_TAB]: ['ClientRequest', 'Request', 'OnboardingRequest'],
}

// Sum the per-entity-type request counts onto one tab.
function sumMessageCounts(byType: Record<string, StageMessageCount>, entityTypes: string[]): StageMessageCount {
  return entityTypes.reduce<StageMessageCount>(
    (acc, t) => {
      const c = byType[t]
      if (c) { acc.unread += c.unread; acc.hasImportant = acc.hasImportant || c.hasImportant }
      return acc
    },
    { unread: 0, hasImportant: false }
  )
}

// The unread-message pill shown on a stage/request tab — red when an unread message is important, else blue.
function renderMessageBadge(count: StageMessageCount | undefined, marginLeft = 6) {
  if (!count || count.unread <= 0) return null
  return (
    <span
      title={`${count.unread} unread message${count.unread === 1 ? '' : 's'}`}
      style={{
        marginLeft,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        borderRadius: 9999,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
        background: count.hasImportant ? '#e5484d' : '#dbe8fb',
        color: count.hasImportant ? '#fff' : '#1e5fc4',
      }}
    >
      {count.unread > 99 ? '99+' : count.unread}
    </span>
  )
}

/** How long an `?tab=ats-placements` link waits on code values before being applied anyway. */
const DEEP_LINK_GATE_TIMEOUT_MS = 4000

/**
 * Hosts the View Designer and refreshes whoever opened it on close.
 *
 * The designer runs under its own throwaway WorkflowProvider (see the mount below), so the
 * `refreshViews` / `refreshConfiguration` that `ViewManager` and `FieldEditPanelInner` call resolve to
 * THAT provider and die with it — and its saves go straight through `useWorkflowApi()`, so no
 * `bumpRefreshSignal` fan-out happens either. Without this, creating a custom field or creating/
 * renaming a view left the host tab's ViewSelector listing the old views and the grid missing the new
 * column until a full page reload.
 *
 * Lives as a child of the designer mount because that mount is inside RequestsGroupStateProvider —
 * WorkflowHub itself renders that provider and so cannot consume it. `bumpRefreshSignal` is what makes
 * the sibling Requests tab providers re-fetch; the two host refreshers come in as props from the root
 * provider.
 */
function DesignerMount({
  onClose,
  refreshHostViews,
  refreshHostConfiguration,
}: {
  onClose: () => void
  refreshHostViews: () => Promise<unknown>
  refreshHostConfiguration: (force?: boolean) => Promise<void>
}) {
  const requestsGroupState = useRequestsGroupState()

  return (
    <ViewManager
      onClose={() => {
        onClose()
        refreshHostViews()
        refreshHostConfiguration(true)
        requestsGroupState?.bumpRefreshSignal(null)
      }}
    />
  )
}

export function WorkflowHub() {
  const { currentStage, setCurrentStage, isFilterDrawerOpen, stages, stageCounts, configuration, refreshViews, refreshConfiguration } = useWorkflow()
  const getLocalizedField = useLocalizedField()
  const { trackStageChange } = useWorkflowTracking()
  const { t, language } = useTranslation()
  const { token, isInternalUser } = useAuth()
  const codeValues = useCodeValues()
  const { isFeatureEnabled, isLoading: isLoadingFeatureFlags } = useFeatureFlags()
  const hasAtsSource = codeValues.isReady && codeValues.getCodeValues("etl.CodeAtsSource", true).length > 0
  const showAtsTab = isFeatureEnabled("atsPlacementReview") && hasAtsSource && isInternalUser
  const showClientRequestsTab = isFeatureEnabled("clientRequestReview")
  // Authorized actions come from the active WorkflowHubConfiguration —
  // ViewVmsRequests is the permission that gates whether the user can see
  // the VMS tab at all (the parent dropdown item). Previously this was
  // an isInternalUser check; switching to a permission-based gate lets
  // org admins grant VMS visibility without bumping the user to internal.
  // Falls back to false when configuration hasn't loaded yet so the tab
  // doesn't flash on/off during mount.
  const authorizedActionCodes = (configuration?.authorizedActions || []).map(
    (a: any) => (typeof a === 'string' ? a : a.actionCode)
  )
  const hasViewVmsRequestsPermission = authorizedActionCodes.includes("ViewVmsRequests")
  const showVmsTab =  isFeatureEnabled('vmsIntegration') && hasViewVmsRequestsPermission

  // Kebab-item gates moved into ViewSelector — the portaled ViewSelector on
  // Request tabs hosts Automations / VMS Settings entries directly under
  // the active per-tab WorkflowProvider, so it can dual-gate on its own
  // feature flags + authorizedAction codes without needing the hub to
  // duplicate the logic.
  const clientTabsEnabledCount = (showAtsTab ? 1 : 0) + (showClientRequestsTab ? 1 : 0) + (showVmsTab ? 1 : 0)
  const [isWorkerSearchOpen, setIsWorkerSearchOpen] = useState(false)
  const [isInboxOpen, setIsInboxOpen] = useState(false)
  const [isInboxNotifOpen, setIsInboxNotifOpen] = useState(false)
  // The inbox's chat opens OUTSIDE the modal (a modal would clip the fixed side panel) — the hub owns it.
  const [inboxChatEntity, setInboxChatEntity] = useState<Pick<InboxConversation, "entityTypeId" | "entityId" | "workerFullName"> | null>(null)
  const [isViewManagerOpen, setIsViewManagerOpen] = useState(false)
  const [activeTab, setActiveTab] = usePersistedState<string | null>('wfh_active_tab_override', null)

  // ── Deep link (?tab= / ?worker=) ─────────────────────────────────────────
  // Landing params from the iframe host. `tab` has to beat the user's last
  // tab, which lives in TWO places: 'wfh_active_tab_override' above (Requests-
  // group tabs) and the provider's 'wfh_active_stage' (WorkOrder stages —
  // resolveInitialStage in workflow-context-simple owns that half, so a stage
  // code has already been applied to currentStage by the time we get here and
  // all this effect does is clear the group override). `worker` is stashed for
  // the tab we're landing on and adopted there as the "Showing: <worker>" pill.
  //
  // Applied once, but deliberately NOT on mount: tab availability is async —
  // showAtsTab waits on code values + feature flags, showVmsTab on the
  // configuration's authorizedActions. Running before those resolve would hand
  // effectiveTab's availability guard a tab it considers unavailable and the
  // deep link would be silently dropped, so hold the intent until the gates
  // can actually answer.
  //
  // While that wait is on, the tab content is held back entirely (see
  // isHoldingForDeepLink at the early return below) — otherwise the user
  // watches their previous tab paint and start fetching, then get replaced.
  const deepLinkAppliedRef = useRef(false)
  const [isDeepLinkResolved, setIsDeepLinkResolved] = useState(false)
  // Peek, don't consume: this runs during render, and the strip belongs in the
  // effect. Keeps returning the tab after the strip, so the hold is released
  // by isDeepLinkResolved rather than by the value disappearing.
  const pendingDeepLinkTab = peekDeepLinkParams().tab

  // codeValues.isReady is driven by an unbounded 500ms poll for
  // window.PhoenixCodeValues, so it can legitimately never resolve. Past this
  // budget we apply the link with whatever the gates currently say, so a
  // missing code-values payload degrades to "lands on the default tab" instead
  // of a permanently blank hub.
  //
  // The question is "does anything this link is about to read depend on code
  // values", not "is this an ATS link". ATS availability is the only gate that
  // reads them, and this effect can consult it for the PENDING tab or — when
  // that tab is absent, gated off for this user, or no longer a known stage
  // code — for the PERSISTED tab, since that's what the worker scope's entity
  // type is resolved from. Race that and the scope stashes under WorkOrder,
  // then code values land, the ATS tab becomes available, and the user is moved
  // there unfiltered with the pill stranded on a grid they can't see.
  //
  // Waiting whenever ATS is on either side is cheaper than deciding up front
  // which one wins — that would mean re-deriving isRequestsTabAvailable and the
  // stage-code match above the gate that feeds them. The only extra wait it
  // buys is an available non-ATS `?tab=` while the persisted tab happens to be
  // ATS, and the shell is already held for that link anyway, under the same
  // DEEP_LINK_GATE_TIMEOUT_MS backstop.
  const dependsOnCodeValues =
    pendingDeepLinkTab?.toLowerCase() === ATS_PLACEMENTS_TAB || activeTab === ATS_PLACEMENTS_TAB

  const [hasGateTimedOut, setHasGateTimedOut] = useState(false)
  useEffect(() => {
    if (!dependsOnCodeValues || isDeepLinkResolved) return
    const timer = setTimeout(() => setHasGateTimedOut(true), DEEP_LINK_GATE_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [dependsOnCodeValues, isDeepLinkResolved])

  // Feature flags and configuration stay MANDATORY — both are bounded (the
  // flags load has a finally, and a configuration that never arrives leaves
  // stages empty, which the early return already covers), so waiting on them
  // can't hang. Timing them out would just answer "unavailable" from data that
  // hadn't loaded yet and drop a perfectly valid link on the wrong tab.
  //
  // Only links that actually read code values wait on them (dependsOnCodeValues
  // above), and only those get the timeout — whose 500ms poll for
  // window.PhoenixCodeValues has no failure path. Everything else never waits.
  const baseGatesResolved = !isLoadingFeatureFlags && !!configuration
  const codeValuesResolved = !dependsOnCodeValues || codeValues.isReady || hasGateTimedOut
  const areTabGatesResolved = baseGatesResolved && codeValuesResolved
  useEffect(() => {
    // Two guards, not one: the ref stops re-entry within this mount, and the
    // module-level flag stops a REMOUNT re-applying an already-consumed link
    // (the parsed params are memoized at module scope, so they outlive us).
    if (deepLinkAppliedRef.current || isDeepLinkApplied()) return
    if (!areTabGatesResolved || stages.length === 0) return
    deepLinkAppliedRef.current = true
    markDeepLinkApplied()
    // Release the shell in this same commit as the setActiveTab below — React
    // batches both into one render, so the hub's first painted tab is already
    // the right one. Set before the early returns so a link we can't honour
    // (or no link at all) still lets the hub render.
    setIsDeepLinkResolved(true)

    const { tab, worker } = readDeepLinkParams()
    if (!tab && !worker) return

    const isRequestsTabAvailable = (value: string) =>
      (value === CLIENT_REQUESTS_TAB && showClientRequestsTab)
      || (value === ATS_PLACEMENTS_TAB && showAtsTab)
      || (value === VMS_PLACEMENTS_TAB && showVmsTab)

    // Which grid the user is about to land in — i.e. the entity type a worker
    // scope has to be stashed under.
    let targetEntityType = ROOT_ENTITY_TYPE

    // Both setActiveTab calls persist ('wfh_active_tab_override'), and that is
    // intended: landing on a tab is a real tab change, and handleTabChange
    // writes exactly the same values — including the null — when the user
    // clicks a stage tab by hand. A deep link that navigated you somewhere and
    // then sent you back on your next visit would be the stranger behaviour.
    // `?tab=` is host-supplied, so match it case-insensitively — both against
    // the Requests-group ids (whose constants are already lowercase) and
    // against stage codes, which is how every other stageCode comparison in
    // this microapp works.
    const requestedTab = tab ? tab.toLowerCase() : null

    if (requestedTab && isRequestsTabAvailable(requestedTab)) {
      setActiveTab(requestedTab)
      targetEntityType = REQUESTS_TAB_ENTITY_TYPES[requestedTab]
    } else if (requestedTab && stages.some(s => s.stageCode.toLowerCase() === requestedTab)) {
      // A WorkOrder stage code: drop the group override so effectiveTab falls
      // through to currentStage, which the provider already pointed at it.
      setActiveTab(null)
    } else {
      // No tab given, or one that's unknown / gated off for this user. Leave
      // the tab alone and scope whatever they land on, so a ?worker=-only link
      // still works and a bad ?tab= degrades to "wrong tab" rather than
      // "no filter at all".
      // Name the accepted values: the host sees only "nothing happened", and
      // a wrong-but-plausible ?tab= is the likeliest way this link fails.
      if (tab) {
        console.warn('workflow-hub: ignoring unavailable ?tab= deep link', {
          tab,
          availableStages: stages.map(s => s.stageCode),
          availableRequestTabs: Object.keys(REQUESTS_TAB_ENTITY_TYPES).filter(isRequestsTabAvailable),
        })
      }
      if (activeTab && isRequestsTabAvailable(activeTab)) {
        targetEntityType = REQUESTS_TAB_ENTITY_TYPES[activeTab]
      }
    }

    // Hand the worker scope to the target grid the same way the worker-search
    // redirect below does — per-entity sessionStorage stash plus the nudge
    // event, because that grid runs its own isolated WorkflowProvider. The
    // stash is declarative (`workerName`, not a built clause): only the target
    // grid can resolve WorkerFullName against ITS field configuration.
    try {
      const key = `wfh_pending_search_filter.${targetEntityType}`
      if (worker) {
        sessionStorage.setItem(key, JSON.stringify({ entityType: targetEntityType, workerName: worker, label: worker }))
      } else if (tab) {
        // An explicit tab link with no worker means "land here unfiltered":
        // drop any scope an earlier hand-off left on that tab this session, or
        // the user arrives at a pill the link never asked for.
        sessionStorage.removeItem(key)
      }
      // The target grid may already be mounted (it is whenever the deep link
      // names the tab the user was already on), in which case nothing in its
      // provider changes and its adoption effect would never re-run.
      window.dispatchEvent(new Event('wfh-pending-search-filter'))
    } catch { /* storage unavailable — we still land on the right tab */ }
    // activeTab is read, not tracked: this runs exactly once, on the first
    // render where the gates can answer, and re-running on the tab change it
    // just made would be a loop.
  }, [areTabGatesResolved, stages, showAtsTab, showVmsTab, showClientRequestsTab, setActiveTab]) // eslint-disable-line react-hooks/exhaustive-deps

  // DOM target for the active Requests-group tab's <ViewSelector /> portal.
  // Rendered as an empty placeholder div in the top-right header area when
  // a Requests-group tab is active (in place of the hub-level WorkOrder
  // ViewSelector); each tab's content reads this via context and portals
  // its ViewSelector here so the dropdown shares the WorkOrder slot but
  // still mounts under the right per-tab WorkflowProvider.
  const [requestsTabHeaderSlot, setRequestsTabHeaderSlot] = useState<HTMLDivElement | null>(null)

  // Per-Request-tab kebab state is no longer held here — the ViewSelector
  // that portals into requestsTabHeaderSlot owns its own Automations modal
  // and VMS Settings panel under the active tab's WorkflowProvider, so
  // these items naturally pick up the right per-tab entityType (incl.
  // VMS sub-tabs). VmsPlacementsTab falls back to its internal settings
  // state when no controlled prop is supplied.

  // While a Requests tab (ATS/VMS/CR) is selected, the stage ROW-count probes are just noise, so pause
  // them there. (The per-stage message badges are NOT paused — they are the reader's own assignment
  // unread, view-independent, so they belong on the Requests tabs too; see useStageMessageCounts below.)
  // Uses raw activeTab (the availability-checked effectiveTab is computed after an early return below,
  // where no hook may live).
  const isRequestsTabActive =
    (activeTab === CLIENT_REQUESTS_TAB && showClientRequestsTab) ||
    (activeTab === ATS_PLACEMENTS_TAB && showAtsTab) ||
    (activeTab === VMS_PLACEMENTS_TAB && showVmsTab)

  // Single source of truth for stage row counts: init fetch + 5-min interval + filter change.
  useStageCountsInit(isRequestsTabActive)

  // Per-stage unread MESSAGE counts for the current user — only when notifications are on (off saves
  // both the my-states and the wfh-search calls). Keyed by stage code like `stageCounts`. Kill-switch:
  // a `disableMessageCounts` Phoenix feature flag that is ABSENT by default (so the badges are on), set
  // to true to turn them — and their two calls — off per tenant/environment. Deliberately its own flag,
  // NOT tied to `messages`, so the badges can be dropped without disabling the messaging feature.
  const { messageNotificationsEnabled } = useMessageNotifications()
  // Gate on baseGatesResolved too: both `isFeatureEnabled("disableMessageCounts")` (false while flags
  // load) and `messageNotificationsEnabled` (?? true until the config lands) default to ON while their
  // sources load, so without this a kill-switched or notifications-off tenant fires the stage-count
  // calls on first paint. `!!configuration` covers the notifications preference too — it arrives in the
  // same SET_CONFIGURATION payload.
  // The feature itself (Mail icon + inbox) — notifications-independent. The count fetches and every count
  // badge additionally require the per-user notifications preference (`messagesInboxEnabled`). So with
  // notifications OFF the Mail icon still shows and the inbox still opens; only the counts are hidden.
  const messagesFeatureEnabled = baseGatesResolved && !isFeatureEnabled("disableMessageCounts")
  const messagesInboxEnabled = messagesFeatureEnabled && messageNotificationsEnabled
  const stageMessageCounts = useStageMessageCounts(messagesInboxEnabled)

  // Per-request-family unread badges for the ATS / VMS / Client-Requests tabs (a lightweight my-states read
  // per type, same gate/cadence as the stage badges). Grouped onto each tab via TAB_MESSAGE_ENTITY_TYPES.
  const requestMessageCounts = useRequestMessageCounts(messagesInboxEnabled)

  // The Mail badge is the total unread across EVERY message surface — the WO stage counts plus every
  // request family — so it mirrors what the inbox actually lists. Both hooks return {} when counts are
  // gated off (notifications off / flag disabled), so this is 0 and the badge hides.
  const totalUnreadMessages =
    Object.values(stageMessageCounts).reduce((sum, c) => sum + (c?.unread ?? 0), 0) +
    Object.values(requestMessageCounts).reduce((sum, c) => sum + (c?.unread ?? 0), 0)
  const tabMessageCount = useCallback(
    (tab: string) => sumMessageCounts(requestMessageCounts, TAB_MESSAGE_ENTITY_TYPES[tab] ?? []),
    [requestMessageCounts]
  )
  // Combined badge for the collapsed Requests dropdown button — every enabled request family's unread.
  const requestsCombinedCount = useMemo(() => {
    const types = [
      ...(showAtsTab ? TAB_MESSAGE_ENTITY_TYPES[ATS_PLACEMENTS_TAB] : []),
      ...(showVmsTab ? TAB_MESSAGE_ENTITY_TYPES[VMS_PLACEMENTS_TAB] : []),
      ...(showClientRequestsTab ? TAB_MESSAGE_ENTITY_TYPES[CLIENT_REQUESTS_TAB] : []),
    ]
    return sumMessageCounts(requestMessageCounts, types)
  }, [requestMessageCounts, showAtsTab, showVmsTab, showClientRequestsTab])

  // Request-row redirect, shared by the worker search and the messages inbox: switch to the family's
  // Requests tab and stash a single-row RequestId scope the tab's grid adopts as its "Showing: …" pill.
  // Callers close their own overlay. Kept here (not duplicated per modal) so the routing rules live once.
  const navigateToRequestTab = useCallback((documentType: string, item: Record<string, any>) => {
    const targetByDocType: Record<string, { tab: string; enabled: boolean }> = {
      AtsPlacementRequest: { tab: ATS_PLACEMENTS_TAB, enabled: showAtsTab },
      VmsRequest: { tab: VMS_PLACEMENTS_TAB, enabled: showVmsTab },
      ClientRequest: { tab: CLIENT_REQUESTS_TAB, enabled: showClientRequestsTab },
    };
    const target = targetByDocType[documentType];
    if (!target?.enabled) return;
    const requestId = String((item as any).requestId ?? '').replace(/'/g, "''");
    if (!requestId) {
      console.warn('Request row has no requestId; redirecting without a row scope', { documentType });
    }
    try {
      const stash = {
        entityType: documentType,
        filter: requestId ? `RequestId eq '${requestId}' and DocumentType eq '${documentType}'` : '',
        label: (item as any).workerFullName || requestId,
      };
      sessionStorage.setItem(`wfh_pending_search_filter.${documentType}`, JSON.stringify(stash));
      window.dispatchEvent(new Event('wfh-pending-search-filter'));
    } catch { /* storage unavailable — tab still switches, just unfiltered */ }
    setActiveTab(target.tab);
  }, [showAtsTab, showVmsTab, showClientRequestsTab, setActiveTab])

  // Single stable getToken — recreated only when the token value changes
  const getToken = useCallback(() => token || "", [token])

  // Contextual-role code → localized label for the message panel's participant pills. The panel is
  // pushed role CODES (access.CodeEntityContextRole, e.g. "AssignedTo"); resolve them to descriptions
  // here, where the code-value source lives, rather than shipping a static list in the package.
  const roleLabels = useMemo(() => {
    if (!codeValues.isReady) return undefined
    const map: Record<string, string> = {}
    for (const item of codeValues.getCodeValues("access.CodeEntityContextRole", false)) {
      if (item.code) map[item.code] = item.description || item.text || item.code
    }
    return map
  }, [codeValues.isReady, codeValues])

  // Single memoized config — new object only when token or role labels change
  const messageProviderConfig = useMemo(
    () => ({ apiEndpoint: env.messagesApiUrl, userToken: getToken, roleLabels }),
    [getToken, roleLabels]
  )

  // Wait until stages and currentStage are set — and, when a `?tab=` link is
  // still pending, until it has been applied. Painting the user's previous tab
  // first would not just flicker: that tab's grid mounts and fires a real
  // search before being torn down again.
  // `isDeepLinkApplied()` also releases the hold on a remount, where the local
  // isDeepLinkResolved is back to false but the link is long since spent.
  const isHoldingForDeepLink = !!pendingDeepLinkTab && !isDeepLinkResolved && !isDeepLinkApplied()
  if (!stages.length || !currentStage || isHoldingForDeepLink) {
    return null; // Optionally, replace with a loading spinner
  }

  // activeTab tracks which tab is visually selected; falls back to currentStage.
  // Ignore a persisted activeTab whose tab is no longer available (e.g. feature flag
  // turned off after the value was saved) so Tabs never mounts with an unmatched value.
  const isPersistedTabAvailable =
    (activeTab === CLIENT_REQUESTS_TAB && showClientRequestsTab) ||
    (activeTab === ATS_PLACEMENTS_TAB && showAtsTab) || 
    (activeTab === VMS_PLACEMENTS_TAB && showVmsTab)
  const effectiveTab = isPersistedTabAvailable ? (activeTab as string) : currentStage
  const isClientRequestsActive = effectiveTab === CLIENT_REQUESTS_TAB
  const isAtsPlacementsActive = effectiveTab === ATS_PLACEMENTS_TAB
  const isVmsPlacementsActive = effectiveTab === VMS_PLACEMENTS_TAB

  // Entity type the View Designer should operate on. The designer is a single hub-level mount, so it
  // has to be told which tab it was opened from — otherwise it inherits the root (WorkOrder) context.
  // VMS resolves to VmsRequest: the designer curates request columns, and VmsEmail has no
  // custom-field/column surface of its own.
  const designerEntityType = isClientRequestsActive
    ? 'ClientRequest'
    : isAtsPlacementsActive
      ? 'AtsPlacementRequest'
      : isVmsPlacementsActive
        ? 'VmsRequest'
        : 'WorkOrder'

  const handleTabChange = (value: string) => {
    if (value === CLIENT_REQUESTS_TAB || value === ATS_PLACEMENTS_TAB || value === VMS_PLACEMENTS_TAB) {
      setActiveTab(value)
      return
    }
    const newStage = value as WorkflowStage
    setActiveTab(null)
    if (newStage !== currentStage) {
      trackStageChange(currentStage, newStage)
    }
    setCurrentStage(newStage)
  }

  // Calculate the maximum width needed for all stage tabs
  const maxTabWidth = Math.max(...stages.map(stage => {
    const title = getLocalizedField(stage, 'title') ?? ''
    // Estimate width based on character count (rough approximation)
    // Each character is roughly 10px + padding
    return title.length * 10 + 64 // 64px for padding (32px on each side)
  }))

  // Fixed width for the entity-selector pill (ATS / VMS / Client Requests).
  // Without this the trigger sizes to its current label, so switching entities
  // changes the pill's width and shoves the stage strip left/right ("twitching").
  // Size it to the widest possible label so all three render identically and the
  // stage tabs stay put. +30 covers the chevron + gap on top of the text/padding
  // estimate (same char-width heuristic as maxTabWidth). The unread badge is NOT added here (that would
  // widen the pill and shift the strip); the button's tightened padding/gap makes room for it instead.
  const entityTabWidth = Math.max(
    ...['ATS', 'VMS', t('clientRequests'), t('requests')].map(s => (s?.length ?? 0) * 10 + 24)
  ) + 30

  const tabStyle = (isActive: boolean) => ({
    background: isActive ? '#FFA96C' : 'transparent',
    color: isActive ? '#222' : '#6B6B6B',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    lineHeight: 1.25,
    fontWeight: isActive ? 700 : 500,
    padding: '10px 32px',
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
    width: maxTabWidth,
    minWidth: maxTabWidth,
    flex: '1 1 0' as const,
    textAlign: 'center' as const,
  })

  return (
    <MessageComponentsProvider config={messageProviderConfig}>
    {/* RequestsGroupStateProvider lives at the workflow-hub level so it
        outlives any of the per-tab WorkflowProviders. Each tab's provider
        publishes its dirty state + view snapshot into this shared store
        on mount and on relevant state changes; the snapshot survives even
        when the tab unmounts on switch, so the save modal can list
        cross-tab unsaved changes for tabs the user opened earlier in the
        session. WorkOrder + non-Requests-group tabs ignore the provider
        (their WorkflowProvider doesn't publish for non-group entities). */}
    <RequestsGroupStateProvider>
    <RequestsTabHeaderSlotProvider slotElement={requestsTabHeaderSlot} onOpenViewManager={() => setIsViewManagerOpen(true)}>
    <div className="flex h-screen overflow-hidden workflow-app-container">
      <div className="flex flex-col w-full overflow-hidden">
        {/* Simple header with navigation breadcrumb */}
        {/* <FBOHeader /> */}

        <main className="flex-1 overflow-hidden bg-white flex flex-col">
          <div className="px-8 py-6 flex-grow flex flex-col overflow-hidden">
            <MaintenanceBanner />
            <Tabs
              value={effectiveTab}
              onValueChange={handleTabChange}
              className="stage-tabs w-full h-full flex flex-col overflow-hidden"
            >
              <div className="stage-tabs-list flex justify-between items-center mb-4">
                <TabsList
                  style={{
                    display: 'flex',
                    background: '#FAF5EF',
                    borderRadius: 8,
                    padding: '0 4px',
                    gap: 0,
                  }}
                >
                  {clientTabsEnabledCount > 1 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          style={{
                            ...tabStyle(isAtsPlacementsActive || isClientRequestsActive || isVmsPlacementsActive),
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            // Tightened from 10/24/20 to reclaim room for the unread badge WITHIN the fixed
                            // width (box-sizing: border-box) — otherwise, for the longest label ("Client
                            // Requests"), the badge pushes the chevron past the edge and it clips. Keeps the
                            // tab width unchanged so the stage strip never shifts.
                            gap: 6,
                            whiteSpace: 'nowrap',
                            // Fixed (not 'auto') so the pill width is identical for
                            // ATS / VMS / Client Requests and the stage strip never shifts.
                            width: entityTabWidth,
                            minWidth: entityTabWidth,
                            flex: '0 0 auto',
                            paddingLeft: 12,
                            paddingRight: 10,
                          }}
                        >
                          {isVmsPlacementsActive
                            ? 'VMS'
                            : isAtsPlacementsActive
                              ? 'ATS'
                              : isClientRequestsActive
                                ? t('clientRequests')
                                : t('requests')}
                          {/* Match the badge to the label: the selected family's own count, or the
                              combined total only under the generic "Requests" label. */}
                          {renderMessageBadge(
                            isVmsPlacementsActive
                              ? tabMessageCount(VMS_PLACEMENTS_TAB)
                              : isAtsPlacementsActive
                                ? tabMessageCount(ATS_PLACEMENTS_TAB)
                                : isClientRequestsActive
                                  ? tabMessageCount(CLIENT_REQUESTS_TAB)
                                  : requestsCombinedCount,
                            0
                          )}
                          <ChevronDown size={16} aria-hidden="true" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {showAtsTab && (
                          <DropdownMenuItem onClick={() => handleTabChange(ATS_PLACEMENTS_TAB)}>
                            ATS
                            {renderMessageBadge(tabMessageCount(ATS_PLACEMENTS_TAB))}
                          </DropdownMenuItem>
                        )}
                        {showClientRequestsTab && (
                          <DropdownMenuItem onClick={() => handleTabChange(CLIENT_REQUESTS_TAB)}>
                            {t('clientRequests')}
                            {renderMessageBadge(tabMessageCount(CLIENT_REQUESTS_TAB))}
                          </DropdownMenuItem>
                        )}
                        {showVmsTab && (
                          <DropdownMenuItem onClick={() => handleTabChange(VMS_PLACEMENTS_TAB)}>
                            VMS
                            {renderMessageBadge(tabMessageCount(VMS_PLACEMENTS_TAB))}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : showAtsTab ? (
                    <TabsTrigger
                      value={ATS_PLACEMENTS_TAB}
                      style={tabStyle(isAtsPlacementsActive)}
                    >
                      ATS
                      {renderMessageBadge(tabMessageCount(ATS_PLACEMENTS_TAB))}
                    </TabsTrigger>
                  ) : showClientRequestsTab ? (
                    <TabsTrigger
                      value={CLIENT_REQUESTS_TAB}
                      style={tabStyle(isClientRequestsActive)}
                    >
                      {t('clientRequests')}
                      {renderMessageBadge(tabMessageCount(CLIENT_REQUESTS_TAB))}
                    </TabsTrigger>
                  ) : showVmsTab ? (
                    // count === 1 path for the VMS-only case. Was missing
                    // before, so users in the (clientRequestReview=off,
                    // atsPlacementReview=off, vmsIntegration/isInternalUser=on)
                    // combo saw the parent tab render nothing — only the
                    // stage strip below appeared, which made the VMS
                    // sub-view unreachable.
                    <TabsTrigger
                      value={VMS_PLACEMENTS_TAB}
                      style={tabStyle(isVmsPlacementsActive)}
                    >
                      VMS
                      {renderMessageBadge(tabMessageCount(VMS_PLACEMENTS_TAB))}
                    </TabsTrigger>
                  ) : null}
                  {clientTabsEnabledCount > 0 && (
                    <div
                      style={{
                        width: 2,
                        alignSelf: 'stretch',
                        margin: '6px 10px',
                        borderRadius: 1,
                        background: '#C4B9AD',
                        flexShrink: 0,
                      }}
                      aria-hidden="true"
                    />
                  )}
                  {stages.map((stage) => (
                    <TabsTrigger
                      key={stage.stageCode}
                      value={stage.stageCode}
                      style={tabStyle(effectiveTab === stage.stageCode)}
                    >
                      {getLocalizedField(stage, 'title')}
                      {stageCounts?.[stage.stageCode.toLowerCase()] != null && (
                        <span style={{ marginLeft: 6, fontWeight: currentStage === stage.stageCode ? 700 : 400, fontSize: 14, opacity: currentStage === stage.stageCode ? 1 : 0.75 }}>
                          ({stageCounts[stage.stageCode.toLowerCase()]})
                        </span>
                      )}
                      {renderMessageBadge(stageMessageCounts[stage.stageCode.toLowerCase()])}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {/* View Selector and Search Button aligned with stage tabs */}
                <div className="flex items-center gap-2">
                  {messagesFeatureEnabled && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setIsInboxOpen(true)}
                            className="relative h-9 w-9"
                            aria-label={t('messages') || 'Messages'}
                          >
                            <Mail size={18} />
                            {/* Count is gated on notifications (the hooks return {} when off → total 0). */}
                            {totalUnreadMessages > 0 && (
                              <span className="absolute -right-[0.15rem] -top-[0.15rem] flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#E5484D] px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
                                {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
                              </span>
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t('messages') || 'Messages'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {ENABLE_WORKER_SEARCH && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setIsWorkerSearchOpen(true)}
                            className="h-9 w-9" 
                            aria-label={t('searchWorkers') || 'Search Workers'}
                          >
                            <Search size={18} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t('searchWorkers') || 'Search Workers'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {!isClientRequestsActive && !isAtsPlacementsActive && !isVmsPlacementsActive
                    ? <ViewSelector onOpenViewManager={() => setIsViewManagerOpen(true)} />
                    : (
                      /* Portal target for the active Requests-group tab's
                         ViewSelector. The tab content renders <ViewSelector />
                         under its own WorkflowProvider and uses createPortal
                         to land it here — same slot WorkOrder uses. ATS still
                         has its bespoke grid (no WorkflowProvider) so its
                         slot stays empty until #236 migrates it. */
                      <div ref={setRequestsTabHeaderSlot} className="flex items-center" />
                    )
                  }
                  {/* No hub-level kebab on Request tabs — the portaled
                      ViewSelector (rendered into requestsTabHeaderSlot above)
                      already exposes Automations + VMS Settings inside its
                      OWN kebab, seeded with the per-tab entityType so it
                      tracks VMS sub-tabs (VmsRequest vs VmsEmail) correctly.
                      Keeping a second kebab here resulted in two adjacent
                      MoreVertical icons AND drove a stale 'VmsRequest'
                      entityType into the modal regardless of which VMS
                      sub-tab was active — both fixed by hosting all menu
                      items inside ViewSelector. The hub still owns the
                      WorkOrder ViewSelector inline on stage tabs (the
                      ternary above), so this branch only needs to portal
                      the slot for Request tabs. */}
                </div>
              </div>

              {/* Client Requests tab */}
              {showClientRequestsTab && (
                <TabsContent value={CLIENT_REQUESTS_TAB} className="flex-1 overflow-hidden">
                  {isClientRequestsActive && <ClientRequestSubTabContent />}
                </TabsContent>
              )}

              {/* ATS Placements tab */}
              {showAtsTab && (
                <TabsContent value={ATS_PLACEMENTS_TAB} className="flex-1 overflow-hidden">
                  {isAtsPlacementsActive && <AtsSubTabContent />}
                </TabsContent>
              )}

              {/* VMS Placements tab */}
              {showVmsTab && (
                <TabsContent value={VMS_PLACEMENTS_TAB} className="flex-1 overflow-hidden">
                  {isVmsPlacementsActive && <VmsPlacementsTab />}
                </TabsContent>
              )}

              {/* Workflow stage tabs */}
              {stages.map((stage) => (
                <TabsContent key={stage.stageCode} value={stage.stageCode} className="flex-1 overflow-hidden">
                  {effectiveTab === stage.stageCode && <WorkflowGrid stage={stage.stageCode as WorkflowStage} />}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </main>
      </div>

      {isFilterDrawerOpen && <FilterDrawer />}
      {ENABLE_WORKER_SEARCH && (
        <WorkerSearchModal
          isOpen={isWorkerSearchOpen}
          onClose={() => setIsWorkerSearchOpen(false)}
          onNavigate={() => { setIsWorkerSearchOpen(false); setActiveTab(null); }}
          // Pre-onboarding result rows: switch to the family's tab and hand a
          // single-row scope off through sessionStorage — the tab mounts its
          // own isolated WorkflowProvider, so the grid adopts it as the
          // dismissible green "Showing: <worker>" pill (temporary filter),
          // exactly like the VmsEmail "View Linked Request" jump. RequestId +
          // DocumentType is the canonical row lookup in the shared
          // client-request index (the index `id` is a SHA-derived key), and
          // it's the only uniform precise scope: VMS/CR have no reference-id
          // grid columns, so a stage-filter equivalent doesn't exist there.
          // Shared request-row routing (see navigateToRequestTab above). The stash persists across the
          // target tab's provider remount so the grid restores the "Showing: …" pill when the user
          // returns; it's cleared only when the pill/filters are cleared.
          onNavigateToRequestTab={(documentType, item) => {
            setIsWorkerSearchOpen(false);
            navigateToRequestTab(documentType, item);
          }}
        />
      )}
      {messagesFeatureEnabled && (
        <MessagesInboxModal
          isOpen={isInboxOpen}
          onClose={() => setIsInboxOpen(false)}
          onOpenSettings={() => setIsInboxNotifOpen(true)}
          onOpenChat={(row) => {
            // Open the chat on the side, OUTSIDE the modal. The inbox stays open (it's non-modal) so the
            // user can jump straight back to the list — clicking another row just swaps the open chat.
            setInboxChatEntity({ entityTypeId: row.entityTypeId, entityId: row.entityId, workerFullName: row.workerFullName })
          }}
          // WO/Assignment redirect: switch to the WorkOrder grid tab (the row filters are already set) and close.
          onAfterNavigate={() => { setIsInboxOpen(false); setActiveTab(null); }}
          // Request-row redirect: hand off to the shared navigator, then close the inbox.
          onNavigateToRequestTab={(documentType, item) => {
            setIsInboxOpen(false);
            navigateToRequestTab(documentType, item);
          }}
        />
      )}
      {inboxChatEntity && (
        // The chat is its OWN modal layer above the inbox: it brings an overlay and closes on
        // click-outside (or Escape / its own X). The inbox stays mounted underneath, so closing the chat
        // lands the user right back on the list.
        <DialogPrimitive.Root open onOpenChange={(open) => { if (!open) setInboxChatEntity(null) }}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay
              className="fixed inset-0 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0"
              style={{ zIndex: Z_INDEX.INBOX_CHAT_OVERLAY }}
            />
            <DialogPrimitive.Content
              aria-describedby={undefined}
              className="fixed right-0 top-0 h-full w-full bg-white shadow-xl outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-right sm:w-[42vw] sm:min-w-[420px]"
              style={{ zIndex: Z_INDEX.INBOX_CHAT_CONTENT }}
            >
              <DialogPrimitive.Title className="sr-only">
                {inboxChatEntity.workerFullName || (t('messages') || 'Messages')}
              </DialogPrimitive.Title>
              <div className="h-full">
                <EntityChatPanel
                  entityTypeId={inboxChatEntity.entityTypeId}
                  entityId={inboxChatEntity.entityId}
                  title={inboxChatEntity.workerFullName || undefined}
                  onClose={() => setInboxChatEntity(null)}
                  showCloseButton
                  language={language}
                  markReadOnOpen={messageNotificationsEnabled}
                  sx={ClutterlessTheme}
                />
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      )}
      <MessageNotificationsModal open={isInboxNotifOpen} onOpenChange={setIsInboxNotifOpen} />
      {isViewManagerOpen && (
        /* The designer stays a SINGLE hub-level mount, but it must operate on the ACTIVE tab's
           entity type — not the root URL-bound WorkOrder context it would otherwise inherit.
           Without these providers every call it makes (fetchViews / saveViewSettings /
           createCustomFieldConfiguration / updateFieldConfiguration) carries entityType=WorkOrder,
           so opening the designer on the VMS tab would silently read and edit WorkOrder config.
           EntityTypeProvider's controlled mode scopes the subtree without touching the URL, and the
           nested WorkflowProvider gives the designer a configuration/views set for that entity —
           the same pairing each Requests tab already uses for its own content.

           suppressRequestsGroupPublish: this mount is INSIDE RequestsGroupStateProvider and shares
           its entityType with the live tab provider, which already publishes there. `tabs` is keyed
           by entityType alone and last-write-wins, so without the opt-out the designer would
           overwrite the real tab's snapshot with its own (default) stage configs — and the cross-tab
           group save would then write those onto the sibling view. */
        <EntityTypeProvider value={designerEntityType}>
          <WorkflowProvider suppressRequestsGroupPublish>
            <DesignerMount
              onClose={() => setIsViewManagerOpen(false)}
              refreshHostViews={refreshViews}
              refreshHostConfiguration={refreshConfiguration}
            />
          </WorkflowProvider>
        </EntityTypeProvider>
      )}
      {/* Per-Request-tab Automations modal was hosted here when the hub had
          its own kebab. It's now owned by the portaled ViewSelector under
          the active per-tab WorkflowProvider, which already passes the
          right entityType (incl. VmsRequest vs VmsEmail) — see the
          AutomationModal block inside view-selector.tsx. */}
    </div>
    </RequestsTabHeaderSlotProvider>
    </RequestsGroupStateProvider>
    </MessageComponentsProvider>
  )
}
